import {
  LAB_TEST_FIELDS,
  PATIENT_FIELDS,
  REAGENT_ITEM_FIELDS,
  REPORT_RESULT_FIELDS,
} from './import-field-lists';
import type { ImportEntity } from './schemas/enums';

/**
 * What each importable entity's columns are.
 *
 * One table, read by four things that would otherwise drift: the mapping
 * screen's column pickers, the generated template workbook, the auto-matcher
 * that guesses a mapping from the sheet's own header row, and the per-entity
 * validators. A field added here appears in all four.
 *
 * Pure data with no imports beyond a type, so the template writer on the server
 * and the wizard in the browser read the same list.
 */

type FieldKind =
  'text' | 'integer' | 'decimal' | 'money' | 'percent' | 'date' | 'boolean' | 'enum' | 'test_list';

export interface ImportField {
  key: string;
  label: string;
  kind: FieldKind;
  /** A row without this is an error, not a warning. */
  required: boolean;
  /**
   * Header spellings that should map to this field automatically.
   *
   * Normalised the same way header cells are (`normaliseName`), so "PACK SIZE",
   * "Pack-Size" and "pack size" all hit the same entry. The `label` itself is
   * always matched and is not repeated here.
   */
  aliases: readonly string[];
  help?: string;
  /** For `kind: 'enum'`. */
  options?: readonly string[];
}

const IMPORT_FIELDS: Record<ImportEntity, readonly ImportField[]> = {
  lab_tests: LAB_TEST_FIELDS,
  reagent_items: REAGENT_ITEM_FIELDS,
  patients: PATIENT_FIELDS,
  report_results: REPORT_RESULT_FIELDS,
};

/** What each entity's rows are, in one line, for the wizard's entity picker. */
export const IMPORT_ENTITY_HINTS: Record<ImportEntity, string> = {
  lab_tests: 'One row per test, with its price. This is the RATE LIST sheet.',
  reagent_items: 'One row per reagent, optionally with what is on the shelf today.',
  patients: 'One row per visit — not per person. A patient with five visits is five rows.',
  report_results: 'One row per parameter of one test of one visit.',
};

export function fieldsFor(entity: ImportEntity): readonly ImportField[] {
  return IMPORT_FIELDS[entity];
}

export function requiredKeys(entity: ImportEntity): string[] {
  return IMPORT_FIELDS[entity].filter((field) => field.required).map((field) => field.key);
}
